import { Controller } from '@hotwired/stimulus'
import { datadogLogs } from '@datadog/browser-logs'

const EMBED_SCRIPT_SRC = 'https://js.hsforms.net/forms/embed/v2.js'

// The embed script is a page-global resource, so its load is memoised at module
// scope rather than per controller instance.
//
// Attaching listeners to a tag that already exists cannot work: `load` and
// `error` fire exactly once, and on a Turbo revisit -- navigate away from the
// page and back, which is the normal path, not an edge case -- they have already
// fired. A fresh listener never runs, the promise never settles, and the visitor
// gets a blank space where the form should be with no error alert either, because
// the catch that reveals it never runs.
let embedScriptLoad = null
let embedInstanceCount = 0

function loadEmbedScript() {
  if (window.hbspt) return Promise.resolve()
  if (embedScriptLoad) return embedScriptLoad

  embedScriptLoad = new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.src = EMBED_SCRIPT_SRC
    script.charset = 'utf-8'
    script.async = true
    script.addEventListener('load', resolve, { once: true })
    script.addEventListener('error', () => reject(new Error('embed script failed to load')), { once: true })
    document.head.appendChild(script)
  }).catch((error) => {
    // Drop the memo so a later visit retries instead of inheriting the failure.
    embedScriptLoad = null
    throw error
  })

  return embedScriptLoad
}

export default class extends Controller {
  static targets = ['embed', 'error', 'booking']

  static values = {
    portalId: String,
    region: String,
    formId: String,
    endpoint: String,
    // Blank means the endpoint takes its parameters flat rather than nested
    // under a form-object key. The two lead forms post to Rails form objects
    // and nest; the newsletter endpoint reads params directly and does not.
    paramKey: String,
    // Values the endpoint needs that the HubSpot form does not collect --
    // per-surface routing like `mailing_list`. Always sent at the top level.
    extraFields: Object,
    // Rails attribute name -> the HubSpot property (or properties) it reads. An
    // array is joined with a space, which is how a form collecting firstname and
    // lastname separately feeds a Rails object that stores a single `name` and
    // splits it back apart.
    fieldMap: Object,
    // Optional booking gate: when the captured value of `gateField` is one of
    // `gateValues`, the booking target is revealed. Both come from ERB so the
    // Ruby constant stays the single source of truth for the rule.
    gateField: String,
    gateValues: Array,
    // Injected into the form's own iframe document -- see #applyFrameCss.
    frameCss: String,
  }

  connect() {
    this.submittedFields = null
    // Reset the alert on every connect. A failed load leaves it visible, and
    // Turbo reconnects the controller on a revisit rather than reloading the
    // page -- so without this the stale error would sit above a form that has
    // since loaded perfectly well.
    if (this.hasErrorTarget) this.errorTarget.classList.add('d-none')

    loadEmbedScript()
      .then(() => this.createForm())
      .catch((error) => this.reportFailure('HubSpot form failed to load', error))
  }

  createForm() {
    window.hbspt.forms.create({
      portalId: this.portalIdValue,
      region: this.regionValue,
      formId: this.formIdValue,
      target: `#${this.embedContainerId}`,
      // Captured on submit rather than read back afterwards: the pre-submit form
      // is the only place every field value is reliably available.
      onFormSubmit: ($form) => { this.submittedFields = this.readFields($form) },
      onFormSubmitted: () => this.handleSubmitted(),
      onFormReady: ($form) => this.applyFrameCss($form),
    })
  }

  // HubSpot renders the form inside its own about:blank iframe, so the page's
  // stylesheet never reaches it and the form falls back to HubSpot's default
  // face. Everything else -- label colour, label size, the submit button -- is
  // set in HubSpot's own form style settings; only the typeface has to come
  // from here, because HubSpot cannot load a font it does not host.
  //
  // Guarded rather than assumed: the iframe is same-origin only because its src
  // is about:blank. Were HubSpot to serve it from their own origin, reading
  // contentDocument would throw, and a form that renders in the wrong face is a
  // far better outcome than one that does not render at all.
  applyFrameCss($form) {
    if (!this.hasFrameCssValue) return

    try {
      const doc = $form?.[0]?.ownerDocument ?? $form?.ownerDocument
      if (!doc?.head || doc === document) return

      const style = doc.createElement('style')
      style.textContent = this.frameCssValue
      doc.head.appendChild(style)
    } catch (error) {
      datadogLogs.logger.warn('HubSpot form frame styling skipped', { error: error.message })
    }
  }

  // Counted, not derived from the form id alone: the same form legitimately
  // appears more than once on a page -- the footer partial is rendered twice,
  // for the desktop and mobile layouts -- and two containers sharing a DOM id
  // would send both embeds to the first one, leaving the second silently blank.
  get embedContainerId() {
    if (!this.embedTarget.id) {
      embedInstanceCount += 1
      this.embedTarget.id = `hubspot-form-${this.formIdValue}-${embedInstanceCount}`
    }

    return this.embedTarget.id
  }

  readFields($form) {
    const form = $form instanceof Element ? $form : $form?.[0]
    if (!form) return null

    const submitted = new FormData(form)

    // HubSpot namespaces some inputs with a positional group prefix, so the
    // property `company_size` renders as name="0-2/company_size" while the
    // standard contact fields render bare. Reading the bare name alone returned
    // empty for every namespaced field, which then failed validation server-side.
    //
    // Matching on the property name rather than the full key also survives a
    // reorder: that `0-2` is a POSITION, so adding or moving a field in the
    // HubSpot editor shifts it, and a hardcoded prefix would silently blank the
    // field again with nothing to notice.
    const read = (name) => {
      let value = submitted.get(name)

      if (value === null) {
        for (const [key, candidate] of submitted.entries()) {
          if (key === name || key.endsWith(`/${name}`)) {
            value = candidate
            break
          }
        }
      }

      return (value || '').toString().trim()
    }
    const fields = {}

    // A map entry is one of three shapes:
    //   "email"                      read that property
    //   ["firstname", "lastname"]    read each and join with a space
    //   { from, values?, wrap? }     read `from`, optionally translate the
    //                                value, optionally wrap it in an array
    //
    // The object form exists because HubSpot sends a dropdown's label as its
    // value. Where Rails already stores a different vocabulary -- Mailchimp
    // tags predating this migration -- the label has to be translated on the
    // way in, or the migration quietly opens a second parallel segment.
    Object.entries(this.fieldMapValue).forEach(([railsName, source]) => {
      if (Array.isArray(source)) {
        fields[railsName] = source.map(read).filter(Boolean).join(' ')
        return
      }

      if (typeof source === 'string') {
        fields[railsName] = read(source)
        return
      }

      const raw = read(source.from)
      const translated = source.values?.[raw] ?? raw
      fields[railsName] = source.wrap === 'array' ? [translated].filter(Boolean) : translated
    })

    return fields
  }

  handleSubmitted() {
    // Revealed from what the visitor entered, independent of the repost result:
    // HubSpot already holds the lead, so a Rails-side failure must not also cost
    // a qualifying company its booking link.
    this.revealBookingIfQualified()
    this.repostToRails()
  }

  revealBookingIfQualified() {
    if (!this.hasBookingTarget || !this.gateFieldValue) return

    const value = this.submittedFields?.[this.gateFieldValue]
    if (!this.gateValuesValue.includes(value)) return

    this.bookingTarget.classList.remove('d-none')
  }

  // HubSpot already holds the lead by this point. The repost exists so the
  // mailers and the Mailchimp write keep running, so a failure here is reported
  // but never surfaced to the visitor -- their submission did land.
  //
  // This is deliberately best-effort, and the tradeoff is worth naming: the
  // browser is the only thing that calls Rails, so connectivity loss or a Rails
  // outage means HubSpot holds a submission Rails never hears about. Nothing is
  // lost -- HubSpot is the system of record and the contact is already there --
  // but reconciliation is manual. Making it guaranteed means a server-side sync
  // from the HubSpot API, which is a larger piece of work than this migration.
  async repostToRails() {
    if (!this.submittedFields) {
      this.reportFailure('HubSpot repost skipped: no captured fields')
      return
    }

    try {
      const response = await fetch(this.endpointValue, {
        method: 'POST',
        // keepalive lets the request finish even if the visitor navigates away or
        // closes the tab mid-flight, which is the likeliest way this repost is
        // lost. It does not make delivery guaranteed -- see the note above.
        keepalive: true,
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-CSRF-Token': this.csrfToken,
        },
        body: JSON.stringify(this.repostPayload),
      })

      if (!response.ok) {
        this.reportFailure(`HubSpot repost rejected with ${response.status}`)
      }
    } catch (error) {
      this.reportFailure('HubSpot repost failed', error)
    } finally {
      this.submittedFields = null
    }
  }

  get repostPayload() {
    const base = { hubspot_form_id: this.formIdValue, ...this.extraFieldsValue }

    return this.paramKeyValue
      ? { ...base, [this.paramKeyValue]: this.submittedFields }
      : { ...base, ...this.submittedFields }
  }

  // eslint-disable-next-line class-methods-use-this
  get csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ''
  }

  reportFailure(message, error) {
    datadogLogs.logger.error(message, { formId: this.formIdValue }, error)

    if (this.hasErrorTarget && !this.submittedFields) {
      this.errorTarget.classList.remove('d-none')
    }
  }
}
